import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  TextField,
  Autocomplete,
  InputAdornment,
  Snackbar,
  Alert,
  Paper,
  Skeleton,
  DialogActions,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { Country, State, City } from "country-state-city";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";

const FieldLabel = ({ children, required = false, width = "140px" }) => (
  <Box
    sx={{
      minWidth: width,
      maxWidth: width,
      display: "flex",
      alignItems: "center",
      fontSize: "13px",
      fontWeight: 400,
      color: "#4B5563",
      pr: 2,
    }}
  >
    {children}
    {required && <span style={{ color: "#E85858", marginLeft: "2px" }}>*</span>}
  </Box>
);

const FormField = ({ children, sx = {} }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      mb: 1.5,
      ...sx,
    }}
  >
    {children}
  </Box>
);

// View Mode Field Component
const ViewField = ({ label, value, isLink = false }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      sx={{
        fontSize: "11px",
        color: "#6B7280",
        mb: 0.5,
        textTransform: "uppercase",
        fontWeight: 600,
        letterSpacing: "0.5px",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "13px",
        color: isLink ? "#3B82F6" : "#1F1F1F",
        fontWeight: isLink ? 500 : 400,
      }}
    >
      {isLink && value ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none", color: "#3B82F6" }}
        >
          {value}
        </a>
      ) : (
        value || "-"
      )}
    </Typography>
  </Box>
);

// Skeleton Field Component
const SkeletonField = () => (
  <Box sx={{ mb: 2 }}>
    <Skeleton variant="text" width="30%" height={16} sx={{ mb: 0.5 }} />
    <Skeleton variant="text" width="80%" height={20} />
  </Box>
);

// Section Skeleton Component
const SectionSkeleton = ({ title, fieldsCount = 3 }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      mb: 2,
      backgroundColor: "#F9FAFB",
      border: "1px solid #E5E7EB",
      borderRadius: "6px",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <Skeleton variant="text" width={150} height={20} />
      <Skeleton variant="circular" width={32} height={32} />
    </Box>
    <Grid container spacing={2}>
      {Array.from(new Array(fieldsCount)).map((_, index) => (
        <Grid item xs={12} sm={6} key={index}>
          <SkeletonField />
        </Grid>
      ))}
    </Grid>
  </Paper>
);

const InstituteViewModal = ({ open, onClose, instituteId, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [instituteData, setInstituteData] = useState(null);

  // Form state
  const [instituteName, setInstituteName] = useState("");
  const [instituteType, setInstituteType] = useState(null);
  const [universityName, setUniversityName] = useState("");
  const [currentInstituteTypes, setCurrentInstituteTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [allBackendStates, setAllBackendStates] = useState([]);
  const [billingStates, setBillingStates] = useState([]);
  const [billingCities, setBillingCities] = useState([]);
  const [billingAttention, setBillingAttention] = useState("");
  const [billingCountry, setBillingCountry] = useState(null);
  const [billingAddressLine1, setBillingAddressLine1] = useState("");
  const [billingAddressLine2, setBillingAddressLine2] = useState("");
  const [billingCity, setBillingCity] = useState(null);
  const [billingState, setBillingState] = useState(null);
  const [billingPincode, setBillingPincode] = useState("");
  const [billingPhone, setBillingPhone] = useState("");
  const [billingFax, setBillingFax] = useState("");
  const [website, setWebsite] = useState("");
  const [gstinNo, setGstinNo] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [newInstituteType, setNewInstituteType] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [studentHold, setStudentHold] = useState("");
  const [instituteLogo, setInstituteLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load institute types, countries and states from backend
  useEffect(() => {
    const fetchStateCountryLists = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/accounts/state_country_lists`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );
        const data = response.data;
        if (data.institute_types) {
          setCurrentInstituteTypes(data.institute_types);
        }
        if (data.country_list) {
          setCountries(data.country_list);
        }
        if (data.state_list) {
          setAllBackendStates(data.state_list);
        }
      } catch (error) {
        console.error("Error fetching state/country lists:", error);
      }
    };
    fetchStateCountryLists();
  }, []);

  // Fetch institute data
  useEffect(() => {
    if (open && instituteId) {
      fetchInstituteData();
    }
  }, [open, instituteId]);

  // Filter states by selected country from backend data
  useEffect(() => {
    if (billingCountry) {
      const filtered = allBackendStates.filter(
        (s) => s.country_id === billingCountry.country_id
      );
      setBillingStates(filtered);
    } else {
      setBillingStates([]);
      setBillingState(null);
    }
  }, [billingCountry, allBackendStates]);

  // Load cities using country-state-city library
  useEffect(() => {
    if (billingCountry && billingState) {
      const cscCountries = Country.getAllCountries();
      const cscCountry = cscCountries.find(
        (c) => c.name.toLowerCase() === billingCountry.country_name.toLowerCase()
      );
      if (cscCountry) {
        const cscStates = State.getStatesOfCountry(cscCountry.isoCode);
        const cscState = cscStates.find(
          (s) => s.name.toLowerCase() === billingState.state_name.toLowerCase()
        );
        if (cscState) {
          const cities = City.getCitiesOfState(cscCountry.isoCode, cscState.isoCode);
          setBillingCities(cities);
        } else {
          setBillingCities([]);
        }
      } else {
        setBillingCities([]);
      }
    } else {
      setBillingCities([]);
    }
  }, [billingState, billingCountry]);

  const fetchInstituteData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/institute/${instituteId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const data = response.data;
      setInstituteData(data);
      populateFormData(data);
    } catch (error) {
      console.error("Error fetching institute data:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch institute details",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const isStudentHoldValid = () => studentHold.trim() !== "" && !isNaN(Number(studentHold));

  const populateFormData = (data) => {
    setInstituteName(data.institute_name || "");
    setInstituteType(data.institute_type || null);
    setUniversityName(data.university_name || "");
    setWebsite(data.website || "");
    setGstinNo(data.gstin_no || "");
    setStudentHold(data.student_hold !== undefined ? String(data.student_hold) : "");
    setLogoPreview(null);
    setInstituteLogo(null);
    setRemoveLogo(false);
    if (data.billing_address) {
      const addr = data.billing_address;
      setBillingAttention(addr.attention || "");
      setBillingAddressLine1(addr.address_line1 || "");
      setBillingAddressLine2(addr.address_line2 || "");
      setBillingPincode(addr.pincode || "");

      if (addr.country_name) {
        const country = countries.find((c) => c.country_name === addr.country_name);
        if (country) {
          setBillingCountry(country);

          setTimeout(() => {
            if (addr.state_name) {
              // Find state from backend states filtered by country
              const filteredStates = allBackendStates.filter(
                (s) => s.country_id === country.country_id
              );
              const state = filteredStates.find((s) => s.state_name === addr.state_name);
              if (state) {
                setBillingState(state);

                setTimeout(() => {
                  if (addr.city) {
                    // Use country-state-city for city lookup
                    const cscCountries = Country.getAllCountries();
                    const cscCountry = cscCountries.find(
                      (c) => c.name.toLowerCase() === country.country_name.toLowerCase()
                    );
                    if (cscCountry) {
                      const cscStates = State.getStatesOfCountry(cscCountry.isoCode);
                      const cscState = cscStates.find(
                        (s) => s.name.toLowerCase() === state.state_name.toLowerCase()
                      );
                      if (cscState) {
                        const cities = City.getCitiesOfState(cscCountry.isoCode, cscState.isoCode);
                        const city = cities.find((c) => c.name === addr.city);
                        if (city) {
                          setBillingCity(city);
                        } else {
                          setBillingCity(addr.city);
                        }
                      } else {
                        setBillingCity(addr.city);
                      }
                    } else {
                      setBillingCity(addr.city);
                    }
                  }
                }, 100);
              }
            }
          }, 100);
        }
      }

      if (addr.phone) {
        const phoneWithoutCode = addr.phone.replace(/^\+\d+-/, "");
        setBillingPhone(phoneWithoutCode);
      }
      if (addr.fax) {
        const faxWithoutCode = addr.fax.replace(/^\+\d+-/, "");
        setBillingFax(faxWithoutCode);
      }
    }
  };

  const handleEditSection = (section) => {
    setEditingSection(section);
    setAttemptedSubmit(false);
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setAttemptedSubmit(false);
    if (instituteData) {
      populateFormData(instituteData);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
   };
   
const handleLogoChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  if (file.size > 1 * 1024 * 1024) {
    setSnackbar({
      open: true,
      message: "Logo must be less than 1MB",
      severity: "error",
    });
    e.target.value = "";
    return;
  }
  setInstituteLogo(file);
  setLogoPreview(URL.createObjectURL(file));
  setRemoveLogo(false);
};

const handleRemoveLogo = () => {
  setInstituteLogo(null);
  setLogoPreview(null);
  setRemoveLogo(true);
};

// const handleSaveLogo = async () => {
//   if (!instituteLogo) return;
//   setIsSubmitting(true);
//   try {
//     const formData = new FormData();
//     formData.append("institute_logo", instituteLogo);

//     await axios.put(
//       `${BASE_URL}/accounts/institute/${instituteId}`,
//       formData,
//       {
//         headers: {
//           "Content-Type": "multipart/form-data",
//           Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
//         },
//       }
//     );

//     setSnackbar({
//       open: true,
//       message: "Logo updated successfully!",
//       severity: "success",
//     });
//     setEditingSection(null);
//     setInstituteLogo(null);
//     setLogoPreview(null);
//     fetchInstituteData();
//     if (onUpdate) onUpdate();
//   } catch (error) {
//     console.error("Error updating logo:", error);
//     setSnackbar({
//       open: true,
//       message:
//         error.response?.data?.message ||
//         "Failed to update logo. Please try again.",
//       severity: "error",
//     });
//   } finally {
//     setIsSubmitting(false);
//   }
// };

const handleSaveLogo = async () => {
  if (!instituteLogo && !removeLogo) {
    setSnackbar({
      open: true,
      message: "Please upload or remove logo before saving",
      severity: "warning",
    });
    return;
  }

  setIsSubmitting(true);
  try {
    const formData = new FormData();

    if (instituteLogo) {
      formData.append("institute_logo", instituteLogo);
    } else if (removeLogo) {
      formData.append("remove_logo", "true");
    }

    await axios.put(
      `${BASE_URL}/accounts/institute/${instituteId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      }
    );

    setSnackbar({
      open: true,
      message: "Logo updated successfully!",
      severity: "success",
    });

    setEditingSection(null);
    setInstituteLogo(null);
    setLogoPreview(null);
    setRemoveLogo(false);
    fetchInstituteData();
    if (onUpdate) onUpdate();
  } catch (error) {
    console.error("Error updating logo:", error);
    setSnackbar({
      open: true,
      message:
        error.response?.data?.message ||
        "Failed to update logo. Please try again.",
      severity: "error",
    });
  } finally {
    setIsSubmitting(false);
  }
};
  const handleCityChange = (event, newValue) => {
    if (!newValue) {
      setBillingCity(null);
      return;
    }
    if (typeof newValue === "string") {
      setBillingCity(newValue);
      return;
    }
    setBillingCity(newValue);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewInstituteType("");
  };

  const handleCreateNewType = () => {
    if (newInstituteType.trim()) {
      setCurrentInstituteTypes([...currentInstituteTypes, newInstituteType]);
      setInstituteType(newInstituteType);
      handleCloseDialog();
    }
  };

  const getPhoneCode = () => {
    if (billingCountry) {
      const cscCountries = Country.getAllCountries();
      const cscCountry = cscCountries.find(
        (c) => c.name.toLowerCase() === billingCountry.country_name.toLowerCase()
      );
      if (cscCountry && cscCountry.phonecode) {
        return `+${cscCountry.phonecode}`;
      }
    }
    return "";
  };

  const isInstituteNameValid = () => instituteName.trim() !== "";
  const isInstituteTypeValid = () => instituteType !== null && instituteType !== "";
  const isCountryValid = () => billingCountry !== null;
  const isStateValid = () => billingState !== null;
  const isAddressLine1Valid = () => billingAddressLine1.trim() !== "";
  const isCityValid = () => billingCity !== null && billingCity !== "";
  const isPincodeValid = () => billingPincode.trim() !== "";

  const validateBasicSection = () => {
  return isInstituteNameValid() && isInstituteTypeValid() && isStudentHoldValid();
  };

  const validateAddressSection = () => {
    return (
      isCountryValid() &&
      isStateValid() &&
      isAddressLine1Valid() &&
      isCityValid() &&
      isPincodeValid()
    );
  };

  const handleSaveSection = async () => {
    setAttemptedSubmit(true);

    if (editingSection === "basic" && !validateBasicSection()) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields marked with *",
        severity: "error",
      });
      return;
    }

    if (editingSection === "address" && !validateAddressSection()) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields marked with *",
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const phoneCode = getPhoneCode();
      const fullPhone = billingPhone ? `${phoneCode}-${billingPhone}` : "";
      const fullFax = billingFax ? `${phoneCode}-${billingFax}` : "";

      const formData = new FormData();
      formData.append("institute_name", instituteName);
      formData.append("institute_type", instituteType || "");
      formData.append("university_name", universityName || instituteName);
      if (studentHold !== "" && studentHold !== null && studentHold !== undefined) {
        formData.append("student_hold", parseInt(studentHold, 10));
      }
      formData.append("billing_address", JSON.stringify({
        attention: billingAttention,
        address_line1: billingAddressLine1,
        address_line2: billingAddressLine2,
        city:
          typeof billingCity === "string" ? billingCity : billingCity?.name,
        state_id: billingState?.state_id,
        state_name: billingState?.state_name,
        country_id: billingCountry?.country_id,
        country_name: billingCountry?.country_name,
        pincode: billingPincode,
        phone: fullPhone,
        fax: fullFax,
      }));
      formData.append("website", website || "");
      formData.append("gstin_no", gstinNo || "");

      await axios.put(
        `${BASE_URL}/accounts/institute/${instituteId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      setSnackbar({
        open: true,
        message: `${
          editingSection === "basic"
            ? "Basic information"
            : editingSection === "address"
            ? "Billing address"
            : "Other details"
        } updated successfully!`,
        severity: "success",
      });

      setEditingSection(null);
      fetchInstituteData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error updating institute:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          "Failed to update institute. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEditingSection(null);
    setAttemptedSubmit(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #E5E7EB",
            px: 3,
            py: 2,
          }}
        >
          <Typography
            sx={{ fontSize: "16px", fontWeight: 600, color: "#1F1F1F" }}
          >
            Institute Details
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 2 }}>
          {loading ? (
            <Box>
              {/* Basic Information Skeleton */}
              <SectionSkeleton title="Basic Information" fieldsCount={3} />

              {/* Billing Address Skeleton */}
              <SectionSkeleton title="Billing Address" fieldsCount={6} />

              {/* Other Details Skeleton */}
              <SectionSkeleton title="Other Details" fieldsCount={2} />
            </Box>
          ) : (
            <Box>
              {/* Basic Information Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 2,
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: "6px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Institute Logo
                  </Typography>

                  {editingSection !== "logo" && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditSection("logo")}
                      sx={{
                        color: "#6B7280",
                        "&:hover": { backgroundColor: "#E5E7EB" },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>

                {editingSection === "logo" ? (
                  <Box>
                    {/* Preview */}
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt="Preview"
                          style={{
                            maxWidth: "260px",
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
                            borderRadius: "8px",
                          }}
                        />
                      ) : instituteData?.institute_logo_url ? (
                        <img
                          src={instituteData.institute_logo_url}
                          alt="Logo"
                          style={{
                            maxWidth: "260px",
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
                            borderRadius: "8px",
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                          No logo uploaded
                        </Typography>
                      )}
                    </Box>

                    {/* Upload */}
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ textTransform: "none", fontSize: "12px", mr: 1 }}
                    >
                      Upload Logo
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                    </Button>

                    {/* Remove */}
                    {(logoPreview || instituteData?.institute_logo_url) && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleRemoveLogo}
                        sx={{ textTransform: "none", fontSize: "12px", color: "red" }}
                      >
                        Remove
                      </Button>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        onClick={handleSaveLogo}
                        disabled={isSubmitting}
                        sx={{
                          textTransform: "none",
                          fontSize: "12px",
                          backgroundColor: "#3B82F6",
                          "&:hover": { backgroundColor: "#2563EB" },
                        }}
                      >
                        Save
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEdit}
                        sx={{
                          textTransform: "none",
                          fontSize: "12px",
                          borderColor: "#D1D5DB",
                          color: "#6B7280",
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {instituteData?.institute_logo_url ? (
                      <img
                        src={instituteData.institute_logo_url}
                        alt="Logo"
                        style={{
                          maxWidth: "260px",
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                        No logo available
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>

              {/* Basic Information Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 2,
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: "6px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Basic Information
                  </Typography>
                  {editingSection !== "basic" && !loading && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditSection("basic")}
                      sx={{
                        color: "#6B7280",
                        "&:hover": { backgroundColor: "#E5E7EB" },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>

                {editingSection === "basic" ? (
                  <Box>
                    <FormField>
                      <FieldLabel required>Institute Name</FieldLabel>
                      <TextField
                        value={instituteName}
                        onChange={(e) => setInstituteName(e.target.value)}
                        placeholder="Enter institute name"
                        size="small"
                        sx={{ flex: 1, maxWidth: "350px" }}
                        autoComplete="off"
                        error={attemptedSubmit && !isInstituteNameValid()}
                        helperText={
                          attemptedSubmit && !isInstituteNameValid()
                            ? "Institute name is required"
                            : ""
                        }
                      />
                    </FormField>

                    <FormField>
                      <FieldLabel required>Institute Type</FieldLabel>
                      <Autocomplete
                        value={instituteType}
                        onChange={(event, newValue) => {
                          if (newValue === "Create New") {
                            handleOpenDialog();
                          } else {
                            setInstituteType(newValue);
                          }
                        }}
                        options={[...currentInstituteTypes, "Create New"]}
                        sx={{ flex: 1, maxWidth: "350px" }}
                        renderOption={(props, option) => {
                          if (option === "Create New") {
                            return (
                              <li
                                {...props}
                                style={{
                                  color: "#3B82F6",
                                  fontWeight: 500,
                                  fontSize: "13px",
                                }}
                              >
                                <AddIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                Create New
                              </li>
                            );
                          }
                          return (
                            <li {...props} style={{ fontSize: "13px" }}>
                              {option}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select institute type"
                            size="small"
                            error={attemptedSubmit && !isInstituteTypeValid()}
                            helperText={
                              attemptedSubmit && !isInstituteTypeValid()
                                ? "Institute type is required"
                                : ""
                            }
                          />
                        )}
                      />
                    </FormField>

                    <FormField>
                      <FieldLabel required>Student Hold</FieldLabel>
                      <TextField
                        value={studentHold}
                        onChange={(e) =>
                          setStudentHold(e.target.value.replace(/[^\d]/g, ""))
                        }
                        size="small"
                        sx={{ flex: 1, maxWidth: "350px" }}
                        autoComplete="off"
                        error={attemptedSubmit && !isStudentHoldValid()}
                        helperText={
                          attemptedSubmit && !isStudentHoldValid()
                            ? "Student hold is required"
                            : ""
                        }
                      />
                    </FormField>

                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        onClick={handleSaveSection}
                        disabled={isSubmitting}
                        sx={{
                          textTransform: "none",
                          fontSize: "12px",
                          backgroundColor: "#3B82F6",
                          "&:hover": { backgroundColor: "#2563EB" },
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEdit}
                        disabled={isSubmitting}
                        sx={{
                          textTransform: "none",
                          fontSize: "12px",
                          borderColor: "#D1D5DB",
                          color: "#6B7280",
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <ViewField label="Institute Name" value={instituteData?.institute_name} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ViewField label="Institute Type" value={instituteData?.institute_type} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ViewField label="Student Hold" value={instituteData?.student_hold} />
                    </Grid>
                  </Grid>
                )}
              </Paper>

              {/* Billing Address Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 2,
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: "6px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Billing Address
                  </Typography>
                  {editingSection !== "address" && !loading && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditSection("address")}
                      sx={{
                        color: "#6B7280",
                        "&:hover": { backgroundColor: "#E5E7EB" },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>

                {editingSection === "address" ? (
                  <Box>
                    <FormField>
                      <FieldLabel>Attention</FieldLabel>
                      <TextField
                        value={billingAttention}
                        onChange={(e) => setBillingAttention(e.target.value)}
                        size="small"
                        sx={{ flex: 1, maxWidth: "300px" }}
                        autoComplete="off"
                      />
                    </FormField>

                    <FormField>
                      <FieldLabel required>Country/Region</FieldLabel>
                      <Autocomplete
                        value={billingCountry}
                        onChange={(event, newValue) => {
                          setBillingCountry(newValue);
                          if (!newValue) {
                            setBillingState(null);
                            setBillingCity(null);
                            setBillingPhone("");
                            setBillingFax("");
                          }
                        }}
                        options={countries}
                        getOptionLabel={(option) => option.country_name || ""}
                        renderOption={(props, option) => (
                          <li {...props} style={{ fontSize: "13px" }}>
                            {option.country_name}
                          </li>
                        )}
                        sx={{ flex: 1, maxWidth: "300px" }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select country"
                            size="small"
                            error={attemptedSubmit && !isCountryValid()}
                            helperText={
                              attemptedSubmit && !isCountryValid()
                                ? "Country is required"
                                : ""
                            }
                          />
                        )}
                      />
                    </FormField>

                    <FormField>
                      <FieldLabel required>State</FieldLabel>
                      <Autocomplete
                        value={billingState}
                        onChange={(event, newValue) => {
                          setBillingState(newValue);
                          if (!newValue) {
                            setBillingCity(null);
                          }
                        }}
                        options={billingStates}
                        getOptionLabel={(option) => option.state_name || ""}
                        disabled={!billingCountry}
                        renderOption={(props, option) => (
                          <li {...props} style={{ fontSize: "13px" }}>
                            {option.state_name}
                          </li>
                        )}
                        sx={{ flex: 1, maxWidth: "300px" }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              billingCountry
                                ? "Select state"
                                : "Select country first"
                            }
                            size="small"
                            error={attemptedSubmit && !isStateValid()}
                            helperText={
                              attemptedSubmit && !isStateValid()
                                ? "State is required"
                                : ""
                            }
                          />
                        )}
                      />
                    </FormField>

                    <FormField>
                      <FieldLabel required>City</FieldLabel>
                      <Autocomplete
                        freeSolo
                        value={billingCity}
                        onChange={handleCityChange}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === "input") {
                            setBillingCity(newInputValue);
                          }
                        }}
                        options={billingCities}
                        getOptionLabel={(option) => {
                          if (typeof option === "string") return option;
                          return option?.name || "";
                        }}
                        filterOptions={(options, { inputValue }) => {
                          if (!inputValue) return options.slice(0, 50);
                          return options
                            .filter((option) =>
                              option.name
                                .toLowerCase()
                                .includes(inputValue.toLowerCase())
                            )
                            .slice(0, 50);
                        }}
                        disabled={!billingState}
                        sx={{ flex: 1, maxWidth: "300px" }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              billingState
                                ? "Enter or select city"
                                : "Select state first"
                            }
                            size="small"
                            error={attemptedSubmit && !isCityValid()}
                            helperText={
                              attemptedSubmit && !isCityValid()
                                ? "City is required"
                                : ""
                            }
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} style={{ fontSize: "13px" }}>
                            {option.name}
                          </li>
                        )}
                      />
                    </FormField>

                    <FormField>
                      <FieldLabel required>Address</FieldLabel>
                      <TextField
                        value={billingAddressLine1}
                        onChange={(e) => setBillingAddressLine1(e.target.value)}
                        placeholder="Street 1"
                        multiline
                        rows={2}
                        maxRows={4}
                        sx={{ flex: 1, maxWidth: "300px" }}
                        autoComplete="off"
                        error={attemptedSubmit && !isAddressLine1Valid()}
                        helperText={
                          attemptedSubmit && !isAddressLine1Valid()
                            ? "Address is required"
                            : ""
                        }
                      />
                    </FormField>

                    <FormField>
                      <FieldLabel width="140px"></FieldLabel>
                      <TextField
                        value={billingAddressLine2}
                        onChange={(e) => setBillingAddressLine2(e.target.value)}
                        placeholder="Street 2"
                        multiline
                        rows={2}
                        maxRows={4}
                        sx={{ flex: 1, maxWidth: "300px" }}
                        autoComplete="off"
                      />
                    </FormField>

                    <FormField>
                      <FieldLabel required>Pin Code</FieldLabel>
                      <TextField
                        value={billingPincode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 10) {
                            setBillingPincode(value);
                          }
                        }}
                        size="small"
                        inputProps={{ maxLength: 10 }}
                        sx={{ flex: 1, maxWidth: "300px" }}
                        autoComplete="off"
                        error={attemptedSubmit && !isPincodeValid()}
                        helperText={
                          attemptedSubmit && !isPincodeValid()
                            ? "Pin code is required"
                            : ""
                        }
                      />
                    </FormField>

                    <FormField>
                      <FieldLabel>Phone</FieldLabel>
                      <TextField
                        value={billingPhone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          setBillingPhone(value);
                        }}
                        placeholder={
                          billingCountry
                            ? "Enter phone number"
                            : "Select country first"
                        }
                        size="small"
                        disabled={!billingCountry}
                        sx={{ flex: 1, maxWidth: "300px" }}
                        autoComplete="off"
                        InputProps={{
                          startAdornment: billingCountry && (
                            <InputAdornment position="start">
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  color: "#6B7280",
                                  fontWeight: 500,
                                }}
                              >
                                {getPhoneCode()}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormField>

                    <FormField>
                      <FieldLabel>Fax Number</FieldLabel>
                      <TextField
                        value={billingFax}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          setBillingFax(value);
                        }}
                        placeholder={
                          billingCountry
                            ? "Enter fax number"
                            : "Select country first"
                        }
                        size="small"
                        disabled={!billingCountry}
                        sx={{ flex: 1, maxWidth: "300px" }}
                        autoComplete="off"
                        InputProps={{
                          startAdornment: billingCountry && (
                            <InputAdornment position="start">
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  color: "#6B7280",
                                  fontWeight: 500,
                                }}
                              >
                                {getPhoneCode()}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormField>

                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        onClick={handleSaveSection}
                        disabled={isSubmitting}
                        sx={{
                          textTransform: "none",
                          fontSize: "12px",
                          backgroundColor: "#3B82F6",
                          "&:hover": { backgroundColor: "#2563EB" },
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEdit}
                        disabled={isSubmitting}
                        sx={{
                          textTransform: "none",
                          fontSize: "12px",
                          borderColor: "#D1D5DB",
                          color: "#6B7280",
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {instituteData?.billing_address?.attention && (
                      <Grid item xs={12} sm={6}>
                        <ViewField
                          label="Attention"
                          value={instituteData.billing_address.attention}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <ViewField
                        label="Address"
                        value={
                          <>
                            {instituteData?.billing_address?.address_line1 ||
                              ""}
                            {instituteData?.billing_address?.address_line2 && (
                              <>
                                <br />
                                {instituteData.billing_address.address_line2}
                              </>
                            )}
                          </>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ViewField
                        label="City"
                        value={instituteData?.billing_address?.city}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ViewField
                        label="State"
                        value={instituteData?.billing_address?.state_name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ViewField
                        label="Country"
                        value={instituteData?.billing_address?.country_name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ViewField
                        label="Pin Code"
                        value={instituteData?.billing_address?.pincode}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ViewField
                        label="Phone"
                        value={instituteData?.billing_address?.phone}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ViewField
                        label="Fax"
                        value={instituteData?.billing_address?.fax}
                      />
                    </Grid>
                  </Grid>
                )}
              </Paper>

              {/* Other Details Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: "6px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Other Details
                  </Typography>
                  {editingSection !== "other" && !loading && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditSection("other")}
                      sx={{
                        color: "#6B7280",
                        "&:hover": { backgroundColor: "#E5E7EB" },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>

                {editingSection === "other" ? (
                  <Box>
                    <FormField>
                      <FieldLabel>Institute Website</FieldLabel>
                      <TextField
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        type="url"
                        size="small"
                        sx={{ flex: 1, maxWidth: "300px" }}
                        autoComplete="off"
                      />
                    </FormField>

                    <FormField>
                      <FieldLabel>GSTIN No.</FieldLabel>
                      <TextField
                        value={gstinNo}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (value.length <= 15) {
                            setGstinNo(value);
                          }
                        }}
                        size="small"
                        inputProps={{ maxLength: 15 }}
                        sx={{ flex: 1, maxWidth: "300px" }}
                        autoComplete="off"
                      />
                    </FormField>

                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        onClick={handleSaveSection}
                        disabled={isSubmitting}
                        sx={{
                          textTransform: "none",
                          fontSize: "12px",
                          backgroundColor: "#3B82F6",
                          "&:hover": { backgroundColor: "#2563EB" },
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEdit}
                        disabled={isSubmitting}
                        sx={{
                          textTransform: "none",
                          fontSize: "12px",
                          borderColor: "#D1D5DB",
                          color: "#6B7280",
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <ViewField
                        label="Website"
                        value={instituteData?.website}
                        isLink={true}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ViewField
                        label="GSTIN No."
                        value={instituteData?.gstin_no}
                      />
                    </Grid>
                  </Grid>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Create New Institute Type Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600, px: 3, py: 2 }}>
          Create New Institute Type
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <TextField
            fullWidth
            value={newInstituteType}
            onChange={(e) => setNewInstituteType(e.target.value)}
            placeholder="Enter new institute type"
            size="small"
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ color: "#6B7280", textTransform: "none", fontSize: "13px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateNewType}
            variant="contained"
            disabled={!newInstituteType.trim()}
            sx={{
              backgroundColor: "#3B82F6",
              "&:hover": { backgroundColor: "#2563EB" },
              textTransform: "none",
              fontSize: "13px",
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InstituteViewModal;
